import { Component, Input, OnInit, OnDestroy, EventEmitter, Output, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, startWith, switchMap, catchError, of as observableOf, map } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';

import { BaseService } from 'src/app/tabms/services/base/base.service';
import { ThemeLayoutService } from 'src/app/@theme/services/theme-layout.service';
import { RTL } from 'src/app/@theme/const';
import { ApiGridInfo, IActualSystemMenuPermission } from 'src/app/tabms/models/base/base';
import { SessionWrapperService } from 'src/app/tabms/services/session/session-wrapper.service';
import { TabSystemMenuService } from 'src/app/tabms/services/menu/tab-system-menu.service';
import { PermissionsListService } from 'src/app/tabms/services/global/permissions-list.service';
import { PhoneFormatPipe } from '../../../pipes/phoneFormat/phone-format.pipe';

/**
 * BaseCardsComponent - Dynamic card view component
 *
 * Follows the same abstraction pattern as base-grid:
 * - Calls gridList API with fielddisplayon=4 (GridAndCard)
 * - Uses returned headers to dynamically render card fields
 * - No hardcoded field names - all configuration from backend
 */
@Component({
  selector: 'app-base-cards',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatPaginatorModule,
    PhoneFormatPipe
  ],
  templateUrl: './base-cards.component.html',
  styleUrls: ['./base-cards.component.scss']
})
export class BaseCardsComponent<T> implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;

  //#region Inputs
  @Input() set service(_service) {
    if (!_service) return;
    this.serviceInstance = _service;
  }

  /** System menu ID passed from parent - preferred over route params */
  @Input() set menuId(id: string) {
    if (id && id !== this._menuId) {
      this._menuId = id;
      this.systemMenuId = id;
      // If component is already initialized, reload data
      if (this._initialized) {
        this.initializeMenuKey();
      }
    }
  }

  @Input() canEdit: boolean = true;
  @Input() canDelete: boolean = true;

  // Conditional action disabling - same as base-grid
  @Input() disableActionsFieldName?: string;
  @Input() disableActionsValue?: any;
  @Input() disableDeleteFieldName?: string;
  @Input() disableDeleteValue?: any;
  //#endregion

  //#region Outputs
  @Output() itemClick = new EventEmitter<T>();
  @Output() editClick = new EventEmitter<T>();
  @Output() deleteClick = new EventEmitter<T>();
  //#endregion

  //#region Public Properties
  serviceInstance: any;
  cardHeaders: ApiGridInfo[] = [];
  cardData: any[] = [];
  totalCount: number = 0;
  loading: boolean = false;
  currentLang: string;
  systemMenuId: string;
  menuKey: string;
  pageParameter: string;
  pageSizes = [5, 10, 25];
  permissions: IActualSystemMenuPermission;
  //#endregion

  //#region Private Properties
  private destroy$: Subject<void> = new Subject<void>();
  private criteriaParams: any = {};
  private _menuId: string;
  private _initialized = false;
  //#endregion

  constructor(
    private baseService: BaseService,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private themeLayoutService: ThemeLayoutService,
    private sessionService: SessionWrapperService,
    private tabSystemMenuService: TabSystemMenuService,
    private permissionsListService: PermissionsListService
  ) {
    this.currentLang = this.baseService.getCurrentLanguage();

    this.themeLayoutService.directionChange.pipe(takeUntil(this.destroy$)).subscribe((dir) => {
      this.currentLang = dir === RTL ? 'ar' : 'en';
    });
  }

  ngOnInit(): void {
    this._initialized = true;

    // If menuId was passed as input, use it; otherwise try to get from route params
    if (this._menuId) {
      this.systemMenuId = this._menuId;
      this.initializeMenuKey();
    } else {
      // Fallback: try to get from route params (for standalone usage)
      this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
        if (params['id'] && !this._menuId) {
          this.systemMenuId = params['id'];
          this.initializeMenuKey();
        }
      });
    }

    // Subscribe to permissions - same as base-grid
    this.permissionsListService
      .getPagePermissions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.permissions = res;
        }
      });

    // Subscribe to criteria changes
    this.baseService.applyCriteria.pipe(takeUntil(this.destroy$)).subscribe((value: Object) => {
      this.criteriaParams = value;
      this.refreshCards();
    });

    // Subscribe to refresh
    this.baseService.refreshTable.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      if (value) {
        this.refreshCards();
      }
    });
  }

  ngAfterViewInit(): void {
    // Wire paginator for server-side pagination - same pattern as base-grid
    if (this.paginator) {
      this.paginator.page.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.loadCardData();
      });
    }
  }

  /**
   * Initialize menuKey following the standard pattern:
   * 1. Try to get from SessionWrapperService (set by base-grid)
   * 2. If not available, fetch from TabSystemMenuService API
   * 3. Store in session for other components
   */
  private initializeMenuKey(): void {
    // First, try to get menuKey from session (may have been set by base-grid)
    const sessionMenuKey = this.sessionService.getMenuKey();

    if (sessionMenuKey) {
      this.menuKey = sessionMenuKey;
      this.pageParameter = 'card';
      this.sessionService.setMenuKeyAndPageParameter(this.menuKey, this.pageParameter);
      this.loadCardData();
    } else if (this.systemMenuId) {
      // Fetch from API like base-grid does
      this.tabSystemMenuService.get(this.systemMenuId).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          this.menuKey = res.result.menukey;
          this.pageParameter = 'card';
          // Store in session for other components
          this.sessionService.setMenuKeyAndPageParameter(this.menuKey, this.pageParameter);
          this.loadCardData();
        },
        error: () => {
          // Fallback: load with systemMenuId if API fails
          this.loadCardData();
        }
      });
    }
  }

  /**
   * Refresh cards - resets paginator and loads data (same pattern as base-grid refreshTable)
   */
  refreshCards(): void {
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadCardData();
  }

  /**
   * Load card data using gridList with fielddisplayon=4 (GridAndCard)
   * Uses paginator values for server-side pagination - same as base-grid
   */
  loadCardData(): void {
    if (!this.serviceInstance) return;

    this.loading = true;
    const hasParams = this.criteriaParams && Object.keys(this.criteriaParams).length > 0;

    // Get pagination values from paginator - same as base-grid
    const pageIndex = this.paginator?.pageIndex ?? 0;
    const pageSize = this.paginator?.pageSize ?? this.pageSizes[0];

    // Call gridList with fielddisplayon=4 for cards
    this.serviceInstance.gridList(
      this.menuKey || this.systemMenuId,
      this.pageParameter,
      4, // fielddisplayon = 4 (GridAndCard)
      pageIndex,
      pageSize,
      hasParams ? this.criteriaParams : null
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        // Extract headers (field configuration for cards)
        // Transform field names: replace dots with underscores to avoid duplicates
        this.cardHeaders = (res?.result?.headers || []).map(header => ({
          ...header,
          fieldname: header.fieldname.replace(/\./g, '_')
        }));

        // Extract and process items - same pattern as base-grid
        const rawItems = res?.result?.items || [];
        this.cardData = rawItems.map(item => {
          const entity = item._entitydto ?? {};

          // Transform keys: replace dots with underscores to match header fieldnames
          const cleanEntity = Object.fromEntries(
            Object.entries(entity).map(([key, value]) => [
              key.replace(/\./g, '_'),
              value
            ])
          );

          // Return item with both original properties and cleaned entity
          return {
            ...item,
            _entitydto: cleanEntity,
            id: item.id,
            ...cleanEntity
          };
        });

        this.totalCount = res?.result?.totalcount || this.cardData.length;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  /**
   * Get display name for a header based on current language
   */
  getHeaderDisplayName(header: ApiGridInfo): string {
    return this.currentLang === 'ar'
      ? (header.displayaraname || header.fieldname)
      : (header.displaylatname || header.fieldname);
  }

  /**
   * Get the display value for a field - handles all field types like base-grid
   */
  getFieldValue(item: any, header: ApiGridInfo): any {
    const value = item[header.fieldname];

    // Handle different field types - same as base-grid
    if (value === null || value === undefined) {
      return '----';
    }

    // Boolean/bit fields
    if (header.fieldtype === 'boolean' || header.fieldtype === 'bit') {
      return value ? '✓' : '✗';
    }

    // Date fields
    if (header.fieldtype === 'date' || header.fieldtype === 'datetime') {
      try {
        const date = new Date(value);
        return date.toLocaleDateString(this.currentLang === 'ar' ? 'ar-EG' : 'en-US');
      } catch {
        return value;
      }
    }

    return value;
  }

  /**
   * Check if field is an image type - same condition as base-grid
   */
  isImageField(header: ApiGridInfo): boolean {
    return header.fieldname === 'profilepic' || header.fieldtype === 'Image';
  }

  /**
   * Check if field is a phone type - same condition as base-grid
   */
  isPhoneField(header: ApiGridInfo): boolean {
    return header.fieldname === 'phonenumber' || header.fieldname.toLowerCase().includes('mobile');
  }

  /**
   * Get image URL with fallback - same as base-grid
   */
  getImageUrl(item: any, header: ApiGridInfo): string {
    const value = item[header.fieldname] ?? item._entitydto?.[header.fieldname];
    return value || 'assets/images/user-placeholder.png';
  }

  /**
   * Get the primary image field for card avatar display
   * Dynamically detects from API headers - same abstraction as base-grid
   */
  getCardImageHeader(): ApiGridInfo | null {
    return this.cardHeaders.find(h => this.isImageField(h)) || null;
  }

  /**
   * Get card avatar image URL - uses same logic as base-grid image column
   */
  getCardAvatarUrl(item: any): string {
    const imageHeader = this.getCardImageHeader();
    if (!imageHeader) {
      return 'assets/images/user-placeholder.png';
    }
    // Access from both cleaned entity and _entitydto for compatibility - same as base-grid
    const value = item[imageHeader.fieldname] ?? item._entitydto?.[imageHeader.fieldname];
    return value || 'assets/images/user-placeholder.png';
  }

  /**
   * Check if card has an image field to display avatar
   */
  hasCardImage(): boolean {
    return this.getCardImageHeader() !== null;
  }

  /**
   * Check if actions are enabled for this item - same as base-grid
   */
  enabledByFieldValue(item: any): boolean {
    const entity = item?._entitydto ?? item;
    return (!this.disableActionsFieldName || entity?.[this.disableActionsFieldName] != this.disableActionsValue);
  }

  /**
   * Check if delete is enabled for this item - same as base-grid
   */
  deleteEnabledByFieldValue(item: any): boolean {
    const entity = item?._entitydto ?? item;
    return (!this.disableDeleteFieldName || entity?.[this.disableDeleteFieldName] != this.disableDeleteValue);
  }

  /**
   * Get the primary display name for a card (first name field found)
   */
  getCardTitle(item: any): string {
    // Try to find a name field from headers
    const nameHeader = this.cardHeaders.find(h =>
      h.fieldname.endsWith('araname') ||
      h.fieldname.endsWith('latname') ||
      h.fieldname === 'name'
    );

    if (nameHeader) {
      return item[nameHeader.fieldname] || '-';
    }

    // Fallback: try common name patterns
    const nameFields = ['araname', 'latname', 'name', 'title'];
    for (const field of nameFields) {
      const key = Object.keys(item).find(k => k.toLowerCase().endsWith(field));
      if (key && item[key]) {
        return item[key];
      }
    }

    return `Item ${item.id || ''}`;
  }

  /**
   * Handle card click
   */
  onCardClick(item: T): void {
    this.itemClick.emit(item);
  }

  /**
   * Handle edit button click
   */
  onEditClick(event: MouseEvent, item: T): void {
    event.stopPropagation();
    this.editClick.emit(item);
  }

  /**
   * Handle delete button click
   */
  onDeleteClick(event: MouseEvent, item: T): void {
    event.stopPropagation();
    this.deleteClick.emit(item);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
